import { useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { Dialog } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { TextField } from '@/components/ui/Field';
import { errorMessage } from '@/api/errors';
import { useAuth } from './useAuth';

/**
 * Access tokens last thirty minutes and the backend has no refresh endpoint. Rather
 * than dropping the user onto the login page mid-task — losing a half-filled claim
 * form, or a crash record they were reading — this asks for the password in place
 * and swaps the token underneath.
 */
export function SessionExpiryModal() {
  const { expiringSoon, user, refreshSession, signOut } = useAuth();
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!expiringSoon || !user) return null;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!user) return;

    setBusy(true);
    setError(null);
    try {
      await refreshSession(user.email, password);
      setPassword('');
      toast.success('Session extended.');
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open
      dismissible={false}
      title="Your session is about to end"
      description="Sign in again to stay on this page. Nothing you are viewing will be lost."
      footer={
        <>
          <Button variant="ghost" onClick={signOut} disabled={busy}>
            Sign out
          </Button>
          <Button variant="primary" type="submit" form="session-refresh" loading={busy}>
            Stay signed in
          </Button>
        </>
      }
    >
      <form id="session-refresh" onSubmit={handleSubmit} className="space-y-3">
        <p className="text-[13px] text-ink-secondary">
          Signed in as <span className="font-medium text-ink">{user.email}</span>
        </p>
        <TextField
          label="Password"
          type="password"
          autoComplete="current-password"
          autoFocus
          required
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          error={error ?? undefined}
        />
      </form>
    </Dialog>
  );
}
